import {
	Stack,
	Flex,
	Text,
	Badge,
	TextInput,
	NumberInput,
	Select,
	ComboboxItem,
} from '@mantine/core';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted';
import { convertCurrencyToSymbol } from '@dealstack/shared/lib/format/currency/currency';
import type { CurrencyType } from '@dealstack/shared/lib/generated';
import { FieldValue } from '@dealstack/shared/lib/aiClient/types';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

interface Props {
	label: string;
	fieldData?: FieldValue | FieldValue[];
	isEditMode: boolean;
	onUpdateValue: (value: string | number | null) => void;
	fieldType?: 'text' | 'number' | 'currency' | 'date';
	currency?: CurrencyType;
	placeholder?: string;
	precision?: number;
	isArrayField?: boolean;
	arrayValues?: FieldValue[];
	selectedValue?: FieldValue;
	onSelectOption?: (index: number) => void;
}

export const TransactionField = ({
	label,
	fieldData,
	isEditMode,
	onUpdateValue,
	fieldType = 'text',
	currency: currencyProp,
	placeholder,
	precision = 2,
	isArrayField = false,
	arrayValues,
	selectedValue,
	onSelectOption,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const showArraySelection = isArrayField && arrayValues && arrayValues.length > 0;
	let effectiveFieldData: FieldValue | undefined;
	if (showArraySelection) {
		effectiveFieldData = selectedValue;
	} else if (Array.isArray(fieldData)) {
		effectiveFieldData = fieldData[0];
	} else {
		effectiveFieldData = fieldData;
	}
	const renderEditField = () => {
		if (fieldType === 'number' || fieldType === 'currency') {
			let numberValue = 0;
			if (typeof effectiveFieldData?.value === 'number') {
				numberValue = effectiveFieldData.value;
			}

			let prefixValue = '';
			if (fieldType === 'currency') {
				prefixValue = currencyProp ? convertCurrencyToSymbol(currencyProp) : '$';
			}

			return (
				<NumberInput
					value={numberValue}
					onChange={(value) => onUpdateValue(value || null)}
					placeholder={
						placeholder ||
						tCapManage('transactions.add_from_document.enter_field_placeholder', {
							field: label.toLowerCase(),
						})
					}
					size="sm"
					min={0}
					decimalScale={precision}
					thousandSeparator=","
					prefix={prefixValue}
				/>
			);
		}

		return (
			<TextInput
				value={(effectiveFieldData?.value || '') as string}
				onChange={(e) => onUpdateValue(e.target.value)}
				placeholder={
					placeholder ||
					tCapManage('transactions.add_from_document.enter_field_placeholder', {
						field: label.toLowerCase(),
					})
				}
				size="sm"
			/>
		);
	};

	const renderDisplayValue = () => {
		if (!effectiveFieldData?.value) {
			return tCapManage('transactions.add_from_document.no_value');
		}

		if (fieldType === 'number' && typeof effectiveFieldData.value === 'number') {
			return effectiveFieldData.value.toLocaleString();
		}

		return effectiveFieldData.value;
	};

	const isCurrencyDisplay =
		!isEditMode &&
		!showArraySelection &&
		fieldType === 'currency' &&
		typeof effectiveFieldData?.value === 'number';

	const renderArraySelection = () => {
		if (!showArraySelection || !arrayValues || !onSelectOption) {
			return null;
		}

		const selectedIndex = selectedValue ? arrayValues.indexOf(selectedValue) : -1;

		const options: ComboboxItem[] = arrayValues.map((item, index) => {
			const displayValue =
				String(item.value) || tCapManage('transactions.add_from_document.empty_value');
			return {
				value: String(index),
				label: displayValue,
			};
		});

		return (
			<Select
				data={options}
				value={String(selectedIndex >= 0 ? selectedIndex : 0)}
				onChange={(value) => {
					const index = parseInt(value || '0', 10);
					onSelectOption(index);
				}}
				size="sm"
				placeholder={tCapManage('transactions.add_from_document.select_value_placeholder')}
				styles={{
					item: {
						fontSize: '14px',
					},
				}}
			/>
		);
	};

	return (
		<Stack gap={2}>
			<Flex align="center" gap="xs">
				<Text size="B4Regular" c={defaultTheme.v3.colors.gray[6]}>
					{label}
				</Text>
				{showArraySelection && arrayValues && (
					<Badge size="xs" color="blue" variant="light">
						{tCapManage('transactions.add_from_document.options_count', {
							count: arrayValues.length,
						})}
					</Badge>
				)}
			</Flex>
			{showArraySelection && !isEditMode && renderArraySelection()}
			{isEditMode && renderEditField()}
			{isCurrencyDisplay && (
				<AmountFormatted
					value={(effectiveFieldData?.value ?? 0) as number}
					currency={currencyProp ?? ('USD' as CurrencyType)}
					showZero
					textProps={{ size: 'B3Regular', fw: 500 }}
				/>
			)}
			{!isEditMode && !showArraySelection && !isCurrencyDisplay && (
				<Text size="B3Regular" fw={500}>
					{renderDisplayValue()}
				</Text>
			)}
		</Stack>
	);
};
